import { useEffect, useState } from "react";
import axios from "axios";
import { PlusCircle, ShoppingCart } from "lucide-react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import { toast } from "react-hot-toast";
import config from "../config";

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await axios.get(`${config.API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data && res.data.wallet?.transactions) {
          setTransactions(res.data.wallet.transactions.reverse());
        } else {
          toast.error("Failed to load transactions.");
        }
      } catch (error) {
        console.error("❌ Error fetching transactions:", error);
        toast.error("Error fetching transactions.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const getTransactionIcon = (type) => {
    if (type === "topup")
      return <PlusCircle className="w-5 h-5 text-green-500" />;
    if (type === "purchase")
      return <ShoppingCart className="w-5 h-5 text-blue-500" />;
    return <PlusCircle className="w-5 h-5 text-gray-400" />;
  };

  const getTransactionLabel = (type) => {
    if (type === "topup") return "Money Added";
    if (type === "purchase") return "Purchase";
    return "Transaction";
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <ProtectDashboardRoute />
      <Sidebar />
      <MobileHeader />

      <div className="flex-1 flex flex-col mt-[65px] lg:mt-0">
        <main className="p-4 md:p-8 flex-1">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Transactions
            </h1>
          </div>

          {loading ? (
            <p className="text-gray-700 text-center py-10">
              Loading transactions...
            </p>
          ) : transactions.length === 0 ? (
            <p className="text-gray-700 text-center py-10">
              No transactions yet.
            </p>
          ) : (
            <>
              {/* 💻 Desktop View */}
              <div className="hidden md:block bg-white rounded-2xl shadow overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="py-4 px-6">Type</th>
                      <th className="py-4 px-6">Amount</th>
                      <th className="py-4 px-6">Method</th>
                      <th className="py-4 px-6">Description</th>
                      <th className="py-4 px-6">Status</th>
                      <th className="py-4 px-6">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, index) => (
                      <tr
                        key={index}
                        className="border-b hover:bg-gray-50 transition"
                      >
                        <td className="py-4 px-6 flex items-center gap-2">
                          {getTransactionIcon(tx.type)}
                          {getTransactionLabel(tx.type)}
                        </td>
                        <td className="py-4 px-6 font-medium text-gray-800">
                          ₦{tx.amount?.toFixed(2)}
                        </td>
                        <td className="py-4 px-6">{tx.method || "N/A"}</td>
                        <td className="py-4 px-6 text-gray-700">
                          {tx.description || "—"}
                        </td>

                        <td className="py-4 px-6">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              tx.status === "successful"
                                ? "bg-green-100 text-green-700"
                                : tx.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {tx.status || "Unknown"}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-600">
                          {new Date(tx.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 📱 Mobile View */}
              <div className="space-y-4 md:hidden">
                {transactions.map((tx, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-xl shadow flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2 font-semibold text-gray-800">
                      {getTransactionIcon(tx.type)}
                      {getTransactionLabel(tx.type)}
                    </div>
                    <p className="text-gray-600">
                      <span className="font-medium">Amount:</span> ₦
                      {tx.amount?.toFixed(2)}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Method:</span>{" "}
                      {tx.method || "N/A"}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Description:</span>{" "}
                      {tx.description || "—"}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Reference:</span>{" "}
                      {tx.reference || "—"}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Date:</span>{" "}
                      {new Date(tx.createdAt).toLocaleString()}
                    </p>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium self-start ${
                        tx.status === "successful"
                          ? "bg-green-100 text-green-700"
                          : tx.status === "pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {tx.status || "Unknown"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
